
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Container, Grid } from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import { useHistory, Redirect } from 'react-router-dom';
import useStyles from '../../../assets/styles/DQProV2Styles';
import PoweredByDQ_Default from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import { AccountCircle, Stars } from '@material-ui/icons';
import DarkModeToggle from '../../../components/DarkModeToggle/DarkModeToggle.comp';
import { IconButton } from '@mui/material';
// import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined';
// import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
// import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
// import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
// import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import { useAuth } from '../../../context/auth';
import { getGuestUser } from '../../../services/auth.service';
import { LOCAL_STORAGE_KEYS, USER_ACCESS_LEVEL } from '../../../utils/constants';
import LoaderWithBackDrop from '../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';

const ALL_FRAMEWORKS = [
    'OECD: Future of Education and Skills 2030',
    'UNESCO: Global Media and Information Literacy Assessment Framework',
    'IEA: International Computer and Information Literacy Study 2018',
    'World Economic Forum: Advancing Digital Safety Framework',
    'UNESCO: A Global Framework of Reference on Digital Literacy Skills',
    'ISTE: ISTE Standards: For Students',
    'Education Testing Services: Framework for ICT Literacy',
    'European Certificate of Digital Literacy (ECDL): ICDL Base Syllabus',
    'DigComp 3.0',
    'UNESCO AI Competency Framework',
    'OECD AI Lit Framework',
];

const DQProV2 = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fwItems, setFwItems] = useState(
        () => ALL_FRAMEWORKS.slice(0, 3).map((text, i) => ({ id: i, text, exiting: false }))
    );
    const fwNextRef = useRef(3);

    useEffect(() => {
        const timer = setInterval(() => {
            setFwItems(prev => [{ ...prev[0], exiting: true }, prev[1], prev[2]]);
            setTimeout(() => {
                const nextText = ALL_FRAMEWORKS[fwNextRef.current % ALL_FRAMEWORKS.length];
                const nextId = fwNextRef.current++;
                setFwItems(prev => [prev[1], prev[2], { id: nextId, text: nextText, exiting: false }]);
            }, 400);
        }, 2000);
        return () => clearInterval(timer);
    }, []);
    const history = useHistory();
    const { setAuthToken, setAuthUser, authUser } = useAuth();

    // Access 25 only — send individual learners to their dashboard.
    // All other users fall through to the existing redirect logic below.
    if (authUser && !authUser.is_guest && authUser.access_level === USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL) {
        return <Redirect to="/dq_pro/individual-dashboard/score-overview" />;
    }

    // An already signed-in (non-guest) user opening the portal lands on their
    // workspace home, the same place /login sends them — not the LearnCreator
    // chat. LearnCreator stays one click away in the sidebar.
    // Peer users with access code 22 are left as before.
    if (authUser && !authUser.is_guest && authUser.access_level !== USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE) {
        return <Redirect to="/my_workspace" />;
    }

    const handleCreateProgram = async () => {
        if (!inputValue.trim()) return;

        // Block Peer users with access code 22 from initiating creation
        if (authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE) {
            return;
        }
        const userMessage = inputValue.trim();
        localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_user_message, userMessage);
        setLoading(true);
        try {
            const result = await getGuestUser();
            if (result.data && result.data.user) {
                setAuthToken(result.data.auth_token);
                setAuthUser(result.data.user);
                localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(result.data.refresh_token));
                localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(result.data.user.email));
                history.push('/dq_pro/program/creation/preview', { userMessage });
            }
        } catch (e) {
            // error already toasted by invokeApi
        } finally {
            setLoading(false);
        }
    };

    // Did You Know stats — hidden for now (Approved Fixes); uncomment to restore.
    // const stats = [
    //     { value: '39%', label: 'of students have experienced cybervictimization',               btnText: 'What About Your Students?',              color: '#FF6900', Icon: SecurityOutlinedIcon },
    //     { value: '26%', label: 'of students have met online strangers offline',                  btnText: 'Create a 45-Minute Lesson',              color: '#EEE921', Icon: PeopleOutlineIcon },
    //     { value: '20%', label: 'of students use AI to cheat on homework',                        btnText: 'Teach Them How to Use AI Ethically',     color: '#3CC6F3', Icon: ReportProblemOutlinedIcon },
    //     { value: '54%', label: 'of students do not use AI tools ethically or responsibly',       btnText: 'Create a 3-Session Program',             color: '#6A8EC9', Icon: ChatBubbleOutlineIcon },
    //     { value: '76%', label: 'of students have experienced at least 1 cyber incident',         btnText: 'Equip Them with Cyber Citizenship Skills', color: '#EA4E99', Icon: LockOutlinedIcon },
    //     { value: '92%', label: 'of students have used generative AI tools for school work',      btnText: 'Do Yours?',                              color: '#B7D334', Icon: EmojiObjectsOutlinedIcon },
    // ];

    const chips = [
        "Can my students use AI tools ethically and responsibly?",
        "Are my students cyber-safe in real scenarios?",
        "Do my learners understand what AI is and how it works?",
        "Can my team apply AI tools productively at work?",
        "How well can learners identify misinformation online?",
        "Do learners know how to respond to cyberbullying situations?",
    ];

    const handleChipClick = (text) => {
        setInputValue(text);
        // Optional: auto-submit or just prefill
    };

    // const handleStatClick = (text) => {
    //     history.push('/signup', { userMessage: text });
    // };

    return (
        <Box className={classes.pagePage} style={isDark ? { backgroundImage: 'none', backgroundColor: '#0a0a0a' } : undefined}>
            {/* Logo bar */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, position: 'relative', backgroundColor: 'var(--brand-surface, #fff)' }}>
                <img src={PoweredByDQ_Logo} alt="Logo" className={classes.logo} />
                <Box sx={{ position: 'absolute', right: 16, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                        onClick={() => history.push('/pbdq/apply')}
                        variant="contained"
                        size="small"
                        startIcon={<Stars style={{ color: '#fff', fontSize: 18 }} />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: '700 !important',
                            fontSize: '15px !important',
                            borderRadius: '999px !important',
                            background: 'linear-gradient(to right, rgba(236, 61, 57, 1), rgba(243, 96, 51, 1)) !important',
                            color: '#fff !important',
                            mr: 2,
                            px: '20px !important',
                            py: '7px !important',
                            boxShadow: 'none !important',
                            '&:hover': {
                                transform: 'scale(1.06)',
                                boxShadow: '0 6px 18px rgba(243, 96, 51, 0.35) !important',
                            },
                        }}
                    >
                        Packages
                    </Button>
                    {/* Theme toggle so users can switch light/dark before signing in */}
                    <DarkModeToggle variant="iconButton" style={{ color: '#FF6900' }} />
                    <IconButton onClick={() => history.push('/login')} sx={{ p: 0 }} style={{
                        zIndex: 1200,
                        border: `2px solid #FF6900`,
                        padding: 6,
                        backgroundColor: 'var(--brand-surface, #fff)',
                    }}>
                        <AccountCircle style={{color: '#FF6900', fontSize: 28}}/>
                    </IconButton>
                </Box>
            </Box>

            {/* Hero Section */}
            <Box className={classes.hero}>
                <Box className={classes.card}>
                    <Box className={classes.heroPill}>
                        <span className={classes.heroPillIcon}>✦</span>
                        AI-Powered Curriculum Generator
                    </Box>
                    <Typography variant="h1" className={classes.title}>
                        Create & Assess AI Literacy Instantly
                    </Typography>
                    <Typography variant="h2" className={classes.titleSub}>
                        - Aligned with Global Standards
                    </Typography>
                    <Typography className={classes.subtitle}>
                        What Skills Do You Want Your Learners to Build & Improve?
                    </Typography>

                    <Box className={classes.inputRow}>
                        <Box className={classes.inputContainer}>
                            <textarea
                                className={classes.textArea}
                                placeholder={isFocused || inputValue ? "" : "I want to see if my 5th grade students can ethically use AI tools..."}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                            />
                            <Button className={classes.createBtn} onClick={handleCreateProgram} disableElevation>
                                {loading ? 'Creating Program...' : 'Create Program'}
                            </Button>
                        </Box>
                        <Box className={classes.frameworksPanel}>
                            <Typography className={classes.frameworksTitle}>
                                100+ international and national<br/>frameworks such as:
                            </Typography>
                            {fwItems.map((item) => (
                                <Box key={item.id} className={item.exiting ? classes.frameworkPillExiting : classes.frameworkPill}>
                                    {item.text}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Typography className={classes.helpText}>
                        <EmojiObjectsOutlinedIcon className={classes.helpTextIcon} />
                        Not sure where to start? Try one of these.
                    </Typography>

                    <Box className={classes.chips}>
                        {chips.map((chip, idx) => (
                            <Box 
                                key={idx} 
                                className={classes.chip}
                                onClick={() => handleChipClick(chip)}
                            >
                                {chip}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Did You Know Section — commented out (Approved Fixes); uncomment to restore.
            <Box className={classes.didYouKnowSection}>
                <Typography className={classes.didYouKnowTitle}>
                    Did You Know?
                </Typography>
                <Box className={classes.statsRow}>
                    {stats.map((stat, idx) => (
                        <Box key={idx} className={classes.statCard}>
                            <stat.Icon style={{ color: stat.color, fontSize: 26 }} />
                            <Typography className={classes.statValue} style={{ color: stat.color }}>
                                {stat.value}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                {stat.label}
                            </Typography>
                            <Button
                                className={classes.statBtn}
                                style={{ backgroundColor: stat.color, color: '#000000' }}
                                onClick={() => handleStatClick(stat.btnText)}
                            >
                                {stat.btnText}
                            </Button>
                        </Box>
                    ))}
                </Box>
            </Box>
            */}

            {/* From Idea to Measurable Impact Section */}
            <Box className={classes.impactSection}>
                <Typography className={classes.impactTitle}>
                    From Idea to Measurable Impact–in Minutes
                </Typography>
                <Typography className={classes.impactSubtitle}>
                    Every program you create comes with built-in measurement,<br />
                    aligned with global standards and benchmarks.
                </Typography>
                <Box className={classes.stepsRow}>
                    <Box className={classes.stepColumn}>
                        <Typography className={classes.stepHeading}>Step 1</Typography>
                        <Typography className={classes.stepDesc}>Tell us the skills you want learners to develop.</Typography>
                        <Typography className={classes.stepBody}>No expertise needed. Describe your goal in plain language — we handle the rest.</Typography>
                        <Box className={classes.stepCard}>
                            <Typography className={classes.stepCardLabel}>YOU MIGHT SAY</Typography>
                            <Box className={classes.stepCardPills}>
                                {['AI safety for teens', 'Online identity', 'Screen habits'].map((pill) => (
                                    <Box key={pill} className={classes.stepCardPill}>{pill}</Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                    <Box className={classes.stepColumn}>
                        <Typography className={classes.stepHeading}>Step 2</Typography>
                        <Typography className={classes.stepDesc}>We automatically build your program.</Typography>
                        <Typography className={classes.stepBody}>A complete, structured learning module — tailored to your learners, ready to deploy.</Typography>
                        <Box className={classes.stepCard} style={{ alignItems: 'center' }}>
                            <Typography className={classes.stepCardLabel}>ALIGNED WITH</Typography>
                            <Box style={{ background: '#FF6900', borderRadius: '14px', padding: '8px 24px', margin: '4px 0' }}>
                                <Typography className={classes.stepCardBigNum} style={{ color: '#fff' }}>100+</Typography>
                            </Box>
                            <Typography className={classes.stepCardSubText} style={{ textAlign: 'center' }}>Digital- &amp; AI-literacy frameworks</Typography>
                        </Box>
                    </Box>
                    <Box className={classes.stepColumn}>
                        <Typography className={classes.stepHeading}>Step 3</Typography>
                        <Typography className={classes.stepDesc}>See exactly how much your learners improved.</Typography>
                        <Typography className={classes.stepBody}>Assessment is built in at every stage — before, during, and after each program.</Typography>
                        <Box className={classes.stepCard}>
                            <Typography className={classes.stepCardLabel}>REAL IMPROVEMENT</Typography>
                            {[
                                { label: 'Before', value: 58, color: '#C91C6E' },
                                { label: 'In-session check', value: 66, color: '#FF6900' },
                                { label: 'After', value: 71, extra: '+13pts', color: '#B7D334' },
                            ].map((item) => (
                                <Box key={item.label} className={classes.stepCardBarGroup}>
                                    <Box className={classes.stepCardBarRow}>
                                        <Box className={classes.stepCardDot} style={{ background: item.color }} />
                                        <Typography className={classes.stepCardBarLabel}>{item.label}</Typography>
                                        <Typography className={classes.stepCardBarValue} style={{ color: item.color }}>
                                            {item.value}{item.extra ? ` ${item.extra}` : ''}
                                        </Typography>
                                    </Box>
                                    <Box className={classes.stepCardBarTrack}>
                                        <Box className={classes.stepCardBarFill} style={{ width: `${item.value}%`, background: item.color }} />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Loader */}
            <LoaderWithBackDrop loading={loading} />
        </Box>
    );
};

export default DQProV2;
